import { WidgetType } from 'widgets/WidgetType.ts';
import {
    FilteredPortfolioChanged,
    PortfolioEventService,
} from 'portfolio/PortfolioEventService.ts';
import {
    buildLogger,
    Logger,
    LoggingService,
} from '@instinet-analytics/core-lib';
import { PortfolioService } from 'portfolio/PortfolioService.ts';
import { FilteredPortfolioChangedEventType } from 'portfolio/store/actions.ts';
import StreamDataService, {
    StreamDataHandlers,
} from 'streamsState/StreamDataService.ts';
import { StreamError } from 'streamsState/store/streamStatuses/StreamErrorState.ts';
import {
    ResponseArrayObjectTypes,
    TxImpactResponse,
} from 'impactModel/types.ts';
import { Store } from 'redux';
import { actions } from 'impactModel/impact-model-slice.ts';
import State from 'store/State.ts';
import { selectUserPreference } from 'userPreferences/userPreferencesSlice.ts';
import { selectChannelId } from 'streamsState/store/streamStatuses/selectors.ts';

const key = (widgetType: WidgetType, widgetKey: string) =>
    `${widgetType}-${widgetKey}`;

/*
 * Manages retrieved of the impact cost estimates from Tradespex when there are interested widgets registered with this service
 */
export class ImpactModelService {
    public store: Store<State> | undefined;
    private registeredKeys: Record<string, true> = {};
    private logger: Logger;
    private portfolioService: PortfolioService;
    private requestedOrders: boolean[] = [];
    private streamDataService: StreamDataService;

    public constructor({
        loggingService,
        portfolioEventService,
        portfolioService,
        streamDataService,
    }: {
        loggingService: LoggingService;
        portfolioEventService: PortfolioEventService;
        portfolioService: PortfolioService;
        streamDataService: StreamDataService;
    }) {
        this.logger = buildLogger('ImpactModelService', loggingService);
        this.portfolioService = portfolioService;
        this.streamDataService = streamDataService;

        portfolioEventService.subscribeFilteredPortfolioChanged(
            this.onFilteredPortfolioChangedAsync.bind(this)
        );
    }

    /*
     * Registers a widget that is interested in the cost estimates
     */
    public register(widgetType: WidgetType, widgetKey: string) {
        const lookupKey = key(widgetType, widgetKey);
        this.logger.debug('Registered key ' + lookupKey);
        this.registeredKeys[lookupKey] = true;

        if (Object.values(this.registeredKeys).length === 1) {
            // First widget...
            this.requestImpactModels();
        }
    }

    /*
     * De-registers a previously registered widget
     */
    public deregister(widgetType: WidgetType, widgetKey: string) {
        const lookupKey = key(widgetType, widgetKey);
        this.logger.debug('Deregistered key ' + lookupKey);
        delete this.registeredKeys[lookupKey];
    }

    private onFilteredPortfolioChangedAsync(
        e: FilteredPortfolioChanged | undefined
    ) {
        setTimeout(() => this.onFilteredPortfolioChanged(e), 0);
    }

    private onFilteredPortfolioChanged(
        ev: FilteredPortfolioChanged | undefined
    ) {
        if (ev?.batchUpdateInProgress) {
            // Wait for the batch fo finish...
            return;
        }
        if (ev?.changedType === FilteredPortfolioChangedEventType.RESET) {
            // New portfolio so we can clear previous errors and limit warnings...
            this.store!.dispatch(actions.setError());
            this.store!.dispatch(actions.setExceedsLimit());
        }
        if (
            ev?.changedType === FilteredPortfolioChangedEventType.UPDATED &&
            ev.updates &&
            ev?.updates.added &&
            ev.updates.added.length === 0
        ) {
            // If no orders added then nothing else to ask for...
            return;
        }
        this.requestImpactModels();
    }

    private requestImpactModels() {
        if (Object.values(this.registeredKeys).length === 0) {
            // Do registered widgets so nothing to do...
            return;
        }

        // Get the orders in the filtered portfolio that do not have a exCostTx widget...
        const filteredOrders = this.portfolioService
            .getFilteredOrders()
            .filter((o) => !(o.exCostTx && o.exCostTx > 0));

        // Ignore those we have already asked for...
        const outstandingOrders = filteredOrders.filter(
            (o) => !this.requestedOrders[o.idx]
        );

        const orderIds = outstandingOrders
            .map((o) => o.orderID)
            .filter((o) => !!o);

        if (orderIds.length === 0) {
            return;
        }

        // Only allow a certain number of orders to be sent to Tradespex....
        const limit = this.getLimit();
        if (orderIds.length > limit) {
            this.logger.debug('Too many orders > ' + limit, orderIds.length);
            this.store!.dispatch(actions.setExceedsLimit(limit));
            return;
        }

        // Remember we have sent these...
        for (const o of outstandingOrders) {
            this.requestedOrders[o.idx] = true;
        }

        // Do it...
        this.sendOrderIds(orderIds);
    }

    private sendOrderIds(orderIds: string[]) {
        this.logger.debug(`Requesting ${orderIds.length} orders`);

        const handlers: StreamDataHandlers<TxImpactResponse> = {
            onMessage: (_streamKey: string, message) => {
                if (message._c === ResponseArrayObjectTypes.Error) {
                    this.logger.warn('Received error response', message);
                    this.store!.dispatch(
                        actions.setError(
                            'Error response from Tradespex service'
                        )
                    );
                } else if (message._c === ResponseArrayObjectTypes.Completed) {
                    // This is just a fyi...
                    this.logger.debug('Received response', message);
                }
            },
            onError: (_streamKey: string, error: StreamError) => {
                this.logger.critical('Received error', error);
                this.store!.dispatch(
                    actions.setError('Error from Tradespex service')
                );
            },
        };

        const streamKey = 'txImpact';
        const currentChannelId = selectChannelId(
            this.store!.getState(),
            streamKey
        );

        this.streamDataService.startAndWatchStream(
            streamKey,
            'startTxImpactStream',
            handlers,
            currentChannelId
        )({ orderIds: orderIds.join(',') });
    }

    private getLimit() {
        const defaultLimit = 1000;
        const limitKey = 'txImpactStreamLimit';
        const userPref = selectUserPreference<number>(
            this.store!.getState(),
            limitKey
        );
        return userPref ?? defaultLimit;
    }
}
